
/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../dataSources/nls/StringResources',
	'../../lib/@waca/core-client/js/core-client/i18n/Formatter'
],
function(_, StringResources, Formatter) {

	/*
	 * Resolve data item references in the forecast statistical details
	 */
	const refResolver = (reference, options) => {
		const { queryResult } = options;

		if (reference.category){
			return reference.category;
		}

		const resultItem = queryResult.getResult().getResultItem(reference.field);
		return resultItem.getDataItemList()[0].getLabel();
	};

	// regex to match numbers in scientific notation. e.g. "1.123e+32"
	const exponentialRegex = new RegExp('[-+]?[0-9]*.?[0-9]+([eE][-+]?[0-9]+)', 'g');
	const HEADER_BACKGROUND = '#e0e0e0';

	const HEADER_DEFAULT_STYLE = {
		'display': 'flex',
		'flex-direction': 'row',
		'align-items': 'center',  //vertical alignment
		'background': HEADER_BACKGROUND
	};
	/**
	 * Item:
	 * {
	 *  	path - Array of strings indicating path to the value of a column
	 *  	resolver - Function to resolve references in stat details payload
	 *  	style - Object describing style to be applied on the column
	 * 			header - header style
	 * 			members - member style
	 *  	value - Value to display display in cell
	 *  	format - Format to be applied to value
	 * }
	 * */
	const HEADER_0 = [
		{ style: { header : HEADER_DEFAULT_STYLE }, value: StringResources.get('forecastDetailsForecastLabel') },
		// ----
		{ style: { header : HEADER_DEFAULT_STYLE }, value: StringResources.get('forecastDetailsModelLabel') },
		{ },
		// ----
		{ style: { header: HEADER_DEFAULT_STYLE }, value: StringResources.get('forecastDetailsQualityMetricLabel') },
		{ },
		{ },
		{ },
		{ },
		{ },
		// ----
		{ style: { header: HEADER_DEFAULT_STYLE },  value: StringResources.get('forecastDetailsSmoothingCoefLabel') },
		{ },
		{ },
		{ },
		{ },
		// ----
		{ style: { header: HEADER_DEFAULT_STYLE },  value: StringResources.get('forecastDetailsDiagnostics') },
		{ },
		{ },
		{ },
		{ },
		{ },
		{ }
	];

	const HEADER_1 = [
		{ path: ['status'], value:  StringResources.get('forecastDetailsStatusLabel') },
		// ----
		{ path: ['model', 'trend', 'type'], value:  StringResources.get('forecastDetailsTrendLabel'), style: { header: { background: HEADER_BACKGROUND } } },
		{ path: ['model', 'season', 'type'], value: StringResources.get('forecastDetailsSeasonLabel'), style: { header: { background: HEADER_BACKGROUND } }  },
		// ----
		{ path: ['accuracy', 'accuracy'], value: StringResources.get('forecastDetailsAccuracyPercentLabel'),  format:{ type: 'percent', minimumFractionDigits: 1 }, style: { }  },
		{ path: ['accuracy', 'MASE'], value: 'MASE',  format:{ type: 'number', minimumFractionDigits: 3 }, style: { }  },
		{ path: ['accuracy', 'MAE'], value: 'MAE',  format:{ type: 'number', minimumFractionDigits: 3 } },
		{ path: ['accuracy', 'RMSE'], value: 'RMSE', format:{ type: 'number', minimumFractionDigits: 3 } },
		{ path: ['accuracy', 'MAPE'], value: 'MAPE',  format:{ type: 'percent', minimumFractionDigits: 1 } },
		{ path: ['accuracy', 'AIC'], value: 'AIC', format:{ type: 'number', minimumFractionDigits: 3 } },
		// ----
		{ path: ['model', 'season', 'period'], value: StringResources.get('forecastDetailsSeasonalPeriodLabel') },
		{ path: ['model', 'parameters', 'alpha'], value:  StringResources.get('forecastDetailsAlphaLabel'), format:{ type: 'number', minimumFractionDigits: 3 }   },
		{ path: ['model', 'parameters', 'beta'], value:  StringResources.get('forecastDetailsBetaLabel'), format:{ type: 'number', minimumFractionDigits: 3 }   },
		{ path: ['model', 'parameters', 'gamma'], value:  StringResources.get('forecastDetailsGammaLabel'), format:{ type: 'number', minimumFractionDigits: 3 }   },
		{ path: ['model', 'parameters', 'phi'], value:  StringResources.get('forecastDetailsPhiLabel'), format:{ type: 'number', minimumFractionDigits: 3 }   },
		// ----
		{ path: ['data', 'missingCount'], value:  StringResources.get('forecastDetailsMissingCount') },
		{ path: ['data', 'seriesLength'], value:  StringResources.get('forecastDetailsSeriesLengthLabel') },
		{ path: ['model', 'ignoredPeriods'], value:  StringResources.get('forecastDetailsIgnoredPeriods') },
		{ path: ['model', 'trend', 'strength'], value:  StringResources.get('forecastDetailsTrendStrength'), format:{ type: 'percent', minimumFractionDigits: 1 } },
		{ path: ['model', 'season', 'strength'], value:  StringResources.get('forecastDetailsSeasonalityStrength'), format:{ type: 'percent', minimumFractionDigits: 1 } },
		{ path: ['data', 'timeDeltaUnit'], value:  StringResources.get('forecastDetailsTimeDeltaUnit') },
		{ path: ['notes'], value:  StringResources.get('forecastDetailsNotes') }
	];
	const HEADER = [HEADER_0, HEADER_1];
	const FIXED_COLUMN_STYLE = {
		members: {
			display: 'flex',
			alignItems: 'center', //vertical alignment
			'background': HEADER_BACKGROUND,
			'border-right': 'none'
		}
	};
	const FIXED_COLUMN =[ { path: ['reference'], resolver: refResolver, style: FIXED_COLUMN_STYLE }];

	class Result {
		constructor(queryResult){
			this.queryResult = queryResult;
			this.statisticalDetails = queryResult.getStatisticalDetails();
			this.formatter = Formatter;
		}

		getNumFixedColumns(){
			return FIXED_COLUMN.length;
		}

		getNumColumns(){
			return HEADER_1.length;
		}

		getNumRows(){
			return this.statisticalDetails.length;
		}

		getHeader(rowIndex, colIndex){
			const headerInfo = HEADER[rowIndex][colIndex];
			const header = {
				style: {
					gridCell: { background: HEADER_BACKGROUND },
					cellValue: {}
				},
				value: headerInfo.value || ''
			};

			if (colIndex + 1 < HEADER[rowIndex].length) {
				// merge empty header cells..
				if (_.isEmpty(HEADER[rowIndex][colIndex + 1].value)) {
					_.extend(header.style.gridCell, {
						'border-right': 'none'  //all headers
					});
				}
			}

			if (_.isEmpty(headerInfo)){
				return header;
			}

			const gridStyleFromDefinition = headerInfo.style && headerInfo.style.header;
			_.extend(header.style.gridCell, gridStyleFromDefinition);


			if (colIndex == 0) {
				_.extend(header.style.gridCell, {
					'border-left': '1px solid #c0bfc0'  //all headers
				});
			}

			header.style.cellValue = { alignment: 'left' };

			return header;
		}

		getNumHeaderRows(){
			return HEADER.length;
		}

		getFixedColumn(rowIndex, colIndex){
			return this._getCell(rowIndex, colIndex, FIXED_COLUMN[colIndex]);
		}

		_getCell(rowIndex, colIndex, info) {
			const headerInfo = info;
			if (_.isUndefined(headerInfo.path)){
				return {
					style: { gridCell: { display: 'none' }, cellValue: { alignment: 'left' } }
				};
			}

			let data = this.statisticalDetails[rowIndex];
			_.each(headerInfo.path, p => data = data[p]);

			if(headerInfo.resolver){
				const option = {
					queryResult: this.queryResult,
					statisticalDetails: this.statisticalDetails
				};
				data = headerInfo.resolver(data, option);
			}

			let shouldFormat = true;
			if(_.isNull(data) || (_.isEmpty(data) && !_.isNumber(data))){
				data = StringResources.get('nullValueText');
				shouldFormat = false;
			}

			if (data.toString().match(exponentialRegex)) {
				shouldFormat = false;
			}

			let gridStyle = headerInfo.style && headerInfo.style.members || {};

			if (colIndex === 0) {
				gridStyle['border-left'] = '1px solid #c0bfc0';

			}

			const styleObj = {
				gridCell: _.extend({ /*border: 'none'*/ }, gridStyle),
				cellValue: { alignment: 'left' }
			};

			return {
				value: shouldFormat ? this.formatter.format(data, headerInfo.format): data,
				style: styleObj
			};
		}

		getCell(rowIndex, colIndex){
			return this._getCell(rowIndex, colIndex, HEADER[1][colIndex]);
		}
	}

	return Result;
});
